import type {
  FunnelDTO,
  CreateFunnelInput,
  UpdateFunnelInput,
  ChannelDTO,
  TestAgentResult,
  WazzupStatusDTO,
  WazzupConnectUrlDTO,
  AmoPipelineDTO,
  AmoPipelineStatusDTO,
  WazzupChannelDTO,
} from "@ai-crm/shared";
import type { AccountInfo } from "./types";

let baseUrl = import.meta.env.VITE_API_URL;
let token = "";

export interface UserMe {
  id: string;
  email: string;
  hasAmoAccount: boolean;
}

interface TokenResponse {
  token: string;
}

interface AmoStatusResponse {
  connected: boolean;
}

interface AmoInstallResponse {
  url: string;
}

export function setApiConfig(url: string, jwt: string) {
  baseUrl = url;
  token = jwt;
}

export function getToken() {
  return token;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const hasBody = options.body != null;
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).error ?? res.statusText,
      res.status,
      (body as Record<string, string>).code ?? "UNKNOWN",
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function userRequest<T>(
  path: string,
  jwt: string,
  options: RequestInit = {},
): Promise<T> {
  const hasBody = options.body != null;
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${jwt}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as Record<string, string>).error ?? res.statusText,
      res.status,
      (body as Record<string, string>).code ?? "UNKNOWN",
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const api = {
  register: (data: { email: string; password: string }) =>
    request<TokenResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  login: (data: { email: string; password: string }) =>
    request<TokenResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getMe: (jwt: string) => userRequest<UserMe>("/auth/me", jwt),

  getAmoStatus: (jwt: string) =>
    userRequest<AmoStatusResponse>("/amo/status", jwt),

  getAmoInstallUrl: (jwt: string) =>
    userRequest<AmoInstallResponse>("/auth/amo/install", jwt),

  getAccountToken: (jwt: string) =>
    userRequest<TokenResponse>("/auth/account-token", jwt),

  getAccount: () => request<AccountInfo>("/api/account"),

  listFunnels: () => request<FunnelDTO[]>("/api/funnels"),

  getFunnel: (id: string) => request<FunnelDTO>(`/api/funnels/${id}`),

  createFunnel: (data: CreateFunnelInput) =>
    request<FunnelDTO>("/api/funnels", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateFunnel: (id: string, data: UpdateFunnelInput) =>
    request<FunnelDTO>(`/api/funnels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteFunnel: (id: string) =>
    request<void>(`/api/funnels/${id}`, { method: "DELETE" }),

  bindChannel: (
    funnelId: string,
    data: { wazzupChannelId: string; type: "TELEGRAM" | "WHATSAPP" },
  ) =>
    request<ChannelDTO>(`/api/funnels/${funnelId}/channels`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  unbindChannel: (funnelId: string, channelId: string) =>
    request<void>(`/api/funnels/${funnelId}/channels/${channelId}`, {
      method: "DELETE",
    }),

  testAgent: (funnelId: string, message: string) =>
    request<TestAgentResult>(`/api/funnels/${funnelId}/test`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  getWazzupStatus: () => request<WazzupStatusDTO>("/api/settings/wazzup"),

  getWazzupConnectUrl: () =>
    request<WazzupConnectUrlDTO>("/api/settings/wazzup/connect-url"),

  completeWazzupAuth: (state: string) =>
    request<WazzupStatusDTO>("/api/settings/wazzup/complete", {
      method: "POST",
      body: JSON.stringify({ state }),
    }),

  saveWazzupKey: (apiKey: string) =>
    request<WazzupStatusDTO>("/api/settings/wazzup", {
      method: "PUT",
      body: JSON.stringify({ apiKey }),
    }),

  listPipelines: () => request<AmoPipelineDTO[]>("/api/amo/pipelines"),

  listPipelineStatuses: (pipelineId: string) =>
    request<AmoPipelineStatusDTO[]>(
      `/api/amo/pipelines/${encodeURIComponent(pipelineId)}/statuses`,
    ),

  listWazzupChannels: () => request<WazzupChannelDTO[]>("/api/wazzup/channels"),
};
